import { useAtom } from 'jotai';
import { ChevronDown, Image, Upload, X } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useModal } from '@/components/modals/new/use-modal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { env } from '@/constants/env';
import { MediaLibraryModal } from '@/features/image-rendering/components/media-library-modal';
import { cn } from '@/lib/utils';
import { createTempId } from '@/utils/tempId';
import {
  renderingsSelectedImageAtom,
  renderingsSelectedPreviewImageAtom,
  renderingsUploadedImagesAtom,
} from '../atoms/image-files';
import { renderingsEstateIdAtom, renderingsProjectIdAtom } from '../atoms/relation';

export const SourceImageCard = () => {
  const { t } = useTranslation();
  const mediaModal = useModal();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [estateId] = useAtom(renderingsEstateIdAtom);
  const [projectId] = useAtom(renderingsProjectIdAtom);
  const [selectedImage, setSelectedImage] = useAtom(renderingsSelectedImageAtom);
  const [, setSelectedPreviewImage] = useAtom(renderingsSelectedPreviewImageAtom);
  const [, setUploadedImages] = useAtom(renderingsUploadedImagesAtom);

  const setImageFile = (file: File) => {
    const next = {
      id: createTempId(),
      originalImage: URL.createObjectURL(file),
      fileType: file.type,
      file,
    };

    setSelectedImage(next);
    setSelectedPreviewImage(next);
    setUploadedImages((prev) => [...prev, next]);
  };

  const setMediaImage = (mediaId: string) => {
    const next = {
      id: createTempId(),
      originalImage: `${env.API_URL}/images/${mediaId}`,
      originalImageId: mediaId,
      fileType: 'image/jpeg',
      file: null,
    };

    setSelectedImage(next);
    setSelectedPreviewImage(next);
    setUploadedImages((prev) => [...prev, next]);
  };

  const openFilePicker = () => inputRef.current?.click();

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    if (file) setImageFile(file);
    e.target.value = '';
  };

  const handleDrop: React.DragEventHandler<HTMLElement> = (e) => {
    e.preventDefault();

    const file = e.dataTransfer.files?.[0];
    if (file?.type.startsWith('image/')) setImageFile(file);
  };

  const handleClear = () => {
    setSelectedImage(undefined);
    setSelectedPreviewImage(undefined);
    setUploadedImages([]);
  };

  const hasSelectedObject = Boolean(estateId || projectId);

  return (
    <>
      <Accordion
        type="single"
        defaultValue="your-room"
        collapsible
        className="w-full overflow-hidden rounded-sm border border-slate-200 bg-white sm:w-[287px]"
      >
        <AccordionItem value="your-room">
          <AccordionTrigger
            arrowIcon={
              <ChevronDown
                aria-hidden
                className="size-4 shrink-0 text-slate-500 transition-transform duration-200"
              />
            }
            className="h-9 px-3 py-0 text-sm font-semibold text-slate-500"
          >
            <span className="flex-1 text-left">
              {t('yourRoom')}
              <span aria-hidden>*</span>
            </span>
          </AccordionTrigger>

          <AccordionContent className="px-3 pb-3 pt-0">
            <label
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              className={cn(
                'flex h-36 w-full cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl bg-slate-100',
                'box-border border border-slate-200 focus-within:border-2 focus-within:border-primary-light hover:border-2 hover:border-primary-light'
              )}
            >
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={handleInputChange}
              />

              {selectedImage?.originalImage ? (
                <img
                  src={selectedImage.originalImage}
                  alt={t('yourRoom')}
                  className="h-full w-full object-cover"
                />
              ) : (
                <>
                  <Upload aria-hidden className="mb-3 size-7 text-slate-400" />
                  <div className="text-center text-[10px] font-medium text-slate-500">
                    {t('drop-image-or-click-to-upload')}
                  </div>
                </>
              )}
            </label>

            <div className="mt-4 flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={openFilePicker}
                className={cn(
                  'h-7 min-w-0 items-center justify-center gap-1.5 rounded-sm border text-center font-medium leading-4 transition-colors [&_svg]:size-4',
                  hasSelectedObject ? 'flex-1 px-2 text-[10px]' : 'w-[261px] px-3 text-[12px]',
                  selectedImage
                    ? 'border-slate-200 bg-[#f7fdfd] text-slate-700 hover:bg-primary-light hover:text-white'
                    : 'border-primary-light bg-primary-light text-white hover:bg-primary-light hover:text-white'
                )}
              >
                <Upload aria-hidden />
                <span className="whitespace-nowrap">
                  {selectedImage ? t('replace') : t('uploadImage')}
                </span>
              </Button>

              {hasSelectedObject ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={mediaModal.open}
                  className="h-7 min-w-0 flex-1 justify-center gap-1.5 rounded-sm border-slate-200 bg-white px-2 text-[10px] font-medium text-slate-500 hover:bg-primary-light hover:text-white hover:opacity-90 active:bg-primary-light active:text-white [&_svg]:size-3.5"
                >
                  <Image aria-hidden />
                  <span className="whitespace-nowrap">{t('fromMedia')}</span>
                </Button>
              ) : null}

              {selectedImage ? (
                <button
                  type="button"
                  onClick={handleClear}
                  aria-label={t('clear')}
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-slate-500 transition-colors',
                    'hover:bg-primary-light hover:text-white hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light/40'
                  )}
                >
                  <X aria-hidden className="size-3.5" />
                </button>
              ) : null}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {mediaModal.isOpen && estateId ? (
        <MediaLibraryModal
          modal={mediaModal}
          type="estate"
          estateId={estateId}
          onSaveSelected={(image) => {
            if (image?.mediaId) setMediaImage(image.mediaId);
          }}
        />
      ) : mediaModal.isOpen && projectId ? (
        <MediaLibraryModal
          modal={mediaModal}
          type="project"
          projectId={projectId}
          onSaveSelected={(image) => {
            if (image?.mediaId) setMediaImage(image.mediaId);
          }}
        />
      ) : null}
    </>
  );
};
